<template>
  <div class="email-branding-page">
    <header class="page-header">
      <router-link to="/settings/organization" class="back-link">
        <i class="fas fa-arrow-left"></i>
        Organization settings
      </router-link>
      <h1 class="page-title">Email branding &amp; templates</h1>
      <p class="page-subtitle">
        Configure how outbound COI upload-request emails look for <strong>every project</strong>
        in this org — From/Reply-To, subject lines, the
        <code>CERTIFICATE HOLDER</code> frame, compliance sign-off, and reusable templates.
      </p>
    </header>

    <div v-if="loading" class="loading-state">
      <i class="fas fa-spinner fa-spin"></i>
      Loading email branding settings…
    </div>

    <template v-else>
      <!-- From / Reply-To -->
      <section class="settings-card">
        <div class="card-header">
          <i class="fas fa-building" aria-hidden="true"></i>
          <div>
            <h2 class="card-title">From / Reply-To</h2>
            <p class="card-desc">Sender identity on every upload-request email from this organization.</p>
          </div>
        </div>

        <div class="card-body">
          <div class="form-row">
            <div class="form-group">
              <label class="form-label">From Email</label>
              <input
                v-model="settings.from_email"
                type="email"
                class="form-control"
                placeholder="no-reply@get-adi.com"
                :disabled="!canEdit"
              >
            </div>
            <div class="form-group">
              <label class="form-label">From Name (display name)</label>
              <input
                v-model="settings.from_name"
                type="text"
                class="form-control"
                placeholder="Viera Builders"
                :disabled="!canEdit"
              >
            </div>
          </div>
          <div class="form-group">
            <label class="form-label">Reply-To</label>
            <input
              v-model="settings.reply_to_email"
              type="email"
              class="form-control"
              placeholder="compliance@yourcompany.com"
              :disabled="!canEdit"
            >
            <span class="help-text">
              Replies will be routed here instead of the From address. Leave blank to use From.
            </span>
          </div>
        </div>
      </section>

      <!-- Sign-off & subject lines -->
      <section class="settings-card">
        <div class="card-header">
          <i class="fas fa-signature" aria-hidden="true"></i>
          <div>
            <h2 class="card-title">Sign-off &amp; subject lines</h2>
            <p class="card-desc">Separate subjects for basic requests, AI-drafted mail, and gap follow-ups.</p>
          </div>
        </div>

        <div class="card-body">
          <div class="form-group">
            <label class="form-label">Compliance Team Sign-off Name</label>
            <input
              v-model="settings.compliance_team_name"
              type="text"
              class="form-control"
              :placeholder="`e.g. ${defaultComplianceTeamPlaceholder}`"
              :disabled="!canEdit"
            >
            <span class="help-text">
              Appears in the "Thank you on behalf of the <em>…</em>" line.
              Leave blank to default to
              <code>{{ defaultComplianceTeamPlaceholder }}</code>.
            </span>
          </div>

          <div class="form-group">
            <label class="form-label">Subject &mdash; basic upload request</label>
            <input
              v-model="settings.upload_request_subject_basic"
              type="text"
              class="form-control"
              :disabled="!canEdit"
            >
            <span class="help-text">
              Variables:
              <code v-pre>{{ client_name }}</code>,
              <code v-pre>{{ project_name }}</code>,
              <code v-pre>{{ vendor_company_name }}</code>,
              <code v-pre>{{ insured_name }}</code>.
            </span>
          </div>

          <div class="form-group">
            <label class="form-label">Subject &mdash; AI / Compliance Health</label>
            <input
              v-model="settings.upload_request_subject_health"
              type="text"
              class="form-control"
              :disabled="!canEdit"
            >
            <span class="help-text">
              Variables:
              <code v-pre>{{ short_reason }}</code>,
              <code v-pre>{{ client_name }}</code>,
              <code v-pre>{{ project_name }}</code>,
              <code v-pre>{{ vendor_company_name }}</code>.
            </span>
          </div>

          <div class="form-group">
            <label class="form-label">Subject &mdash; gap follow-up</label>
            <input
              v-model="settings.upload_request_subject_gap"
              type="text"
              class="form-control"
              :disabled="!canEdit"
            >
            <span class="help-text">
              Sent automatically when a comprehensive check completes with failures.
              Variables:
              <code v-pre>{{ gap_count }}</code>,
              <code v-pre>{{ vendor_company_name }}</code>,
              <code v-pre>{{ client_name }}</code>,
              <code v-pre>{{ project_name }}</code>.
            </span>
          </div>
        </div>
      </section>

      <!-- Email frame -->
      <section class="settings-card">
        <div class="card-header">
          <i class="fas fa-grip-lines" aria-hidden="true"></i>
          <div>
            <h2 class="card-title">Email frame (header &amp; footer)</h2>
            <p class="card-desc">
              Deterministic wrapper around every upload email. Leave blank to use the platform default wording.
            </p>
          </div>
        </div>

        <div class="card-body">
          <div class="form-group">
            <label class="form-label">Header template</label>
            <textarea
              v-model="settings.email_frame_header_template"
              class="form-control monospace"
              rows="10"
              :disabled="!canEdit"
              :placeholder="defaultHeaderPlaceholder"
            ></textarea>
            <span class="help-text">
              Variables:
              <code v-pre>{{ recipient_salutation }}</code>,
              <code v-pre>{{ client_name }}</code>,
              <code v-pre>{{ vendor_company_name }}</code>,
              <code v-pre>{{ insured_name }}</code>,
              <code v-pre>{{ certificate_holder_name }}</code>,
              <code v-pre>{{ certificate_holder_address }}</code>,
              <code v-pre>{{ project_name }}</code>,
              <code v-pre>{{ is_gap_notification }}</code>,
              <code v-pre>{{ gap_count }}</code>.
            </span>
          </div>

          <div class="form-group">
            <label class="form-label">Footer template</label>
            <textarea
              v-model="settings.email_frame_footer_template"
              class="form-control monospace"
              rows="10"
              :disabled="!canEdit"
              :placeholder="defaultFooterPlaceholder"
            ></textarea>
            <span class="help-text">
              Variables:
              <code v-pre>{{ upload_url }}</code>,
              <code v-pre>{{ deadline_days }}</code>,
              <code v-pre>{{ compliance_team_name }}</code>,
              <code v-pre>{{ contact_name }}</code>,
              <code v-pre>{{ contact_title }}</code>,
              <code v-pre>{{ contact_email }}</code>.
            </span>
          </div>

          <div class="form-group">
            <label class="form-label">
              AI tone / style guide
              <span class="help-inline">(optional, free-form)</span>
            </label>
            <textarea
              v-model="settings.notification_style_guide"
              class="form-control"
              rows="4"
              :disabled="!canEdit"
              placeholder="e.g. Keep tone professional and direct. Reference our 7-day SLA. Always thank the agent of record."
            ></textarea>
            <span class="help-text">
              Reference text the AI draft generator reads when authoring the middle of
              the email. Use the Header / Footer fields above for strict structural
              control.
            </span>
          </div>
        </div>
      </section>

      <div class="save-bar">
        <button
          class="btn btn-primary"
          :disabled="!canEdit || saving"
          @click="saveSettings"
        >
          <i class="fas fa-save"></i>
          {{ saving ? 'Saving…' : 'Save email branding' }}
        </button>
        <span v-if="saveMsg" class="save-msg" :class="saveMsgType">
          <i :class="saveMsgType === 'success' ? 'fas fa-check-circle' : 'fas fa-exclamation-triangle'"></i>
          {{ saveMsg }}
        </span>
        <p v-if="!canEdit" class="permission-note">
          <i class="fas fa-lock"></i>
          Organization admin required to edit these settings.
        </p>
      </div>

      <!-- Templates -->
      <section class="settings-card">
        <div class="card-header">
          <i class="fas fa-envelope" aria-hidden="true"></i>
          <div>
            <h2 class="card-title">Email templates</h2>
            <p class="card-desc">
              Org-owned templates plus platform defaults. Projects reference templates by slug in COI email configuration.
            </p>
          </div>
        </div>

        <div class="card-body">
          <div class="templates-list">
            <div
              v-for="template in templates"
              :key="template.id"
              class="template-row"
              :class="templateRowClass(template)"
            >
              <div class="template-info">
                <div class="template-name">
                  <span>{{ template.name }}</span>
                  <span v-if="template.is_default_for_category" class="row-label row-label--default">Default</span>
                  <span v-if="!template.owned_by_org" class="row-label row-label--muted">Platform</span>
                  <span v-if="!template.is_active" class="row-label row-label--inactive">Inactive</span>
                </div>
                <div class="template-meta">
                  <code class="template-slug">{{ template.slug }}</code>
                  <span class="template-subject">
                    <i class="fas fa-tag"></i>
                    {{ template.subject_template }}
                  </span>
                </div>
              </div>
              <div class="template-actions">
                <button
                  class="btn btn-sm btn-outline"
                  @click="previewTemplate(template)"
                  title="Preview"
                >
                  <i class="fas fa-eye"></i>
                </button>
                <button
                  v-if="template.owned_by_org && canEdit"
                  class="btn btn-sm btn-outline"
                  @click="editTemplate(template)"
                  title="Edit"
                >
                  <i class="fas fa-pen"></i>
                </button>
                <button
                  v-if="canEdit"
                  class="btn btn-sm btn-outline"
                  @click="cloneTemplate(template)"
                  :title="template.owned_by_org ? 'Duplicate' : 'Clone to customize'"
                >
                  <i class="fas fa-copy"></i>
                </button>
                <button
                  v-if="template.owned_by_org && canEdit"
                  class="btn btn-sm btn-outline btn-danger"
                  @click="deleteTemplate(template)"
                  :disabled="template.is_default_for_category"
                  :title="template.is_default_for_category ? 'Cannot delete the default template' : 'Delete'"
                >
                  <i class="fas fa-trash"></i>
                </button>
              </div>
            </div>
          </div>

          <div v-if="!templates.length" class="empty-state">
            <i class="fas fa-envelope-open"></i>
            <p>No templates yet. The COI Upload Request platform default will be installed automatically.</p>
          </div>
        </div>
      </section>
    </template>

    <!-- ───── Edit / Clone modal ────────────────────────────────────── -->
    <div v-if="editingTemplate" class="modal-overlay" @click.self="closeEditor">
      <div class="modal-content">
        <div class="modal-header">
          <h3>{{ editingTemplate.id ? 'Edit Template' : 'Clone Template' }}</h3>
          <button class="modal-close" @click="closeEditor">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="form-row">
            <div class="form-group">
              <label class="form-label">Name</label>
              <input v-model="editingTemplate.name" type="text" class="form-control">
            </div>
            <div class="form-group">
              <label class="form-label">Slug</label>
              <input
                v-model="editingTemplate.slug"
                type="text"
                class="form-control"
                :disabled="!!editingTemplate.id"
              >
              <span v-if="editingTemplate.id" class="help-text">
                Slug is immutable on existing templates.
              </span>
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label class="form-label">Category</label>
              <select v-model="editingTemplate.category_id" class="form-control">
                <option v-for="c in categories" :key="c.id" :value="c.id">
                  {{ c.name }}
                </option>
              </select>
            </div>
            <div class="form-group form-group--checkbox-row">
              <label class="checkbox-label">
                <input type="checkbox" v-model="editingTemplate.is_active">
                Active
              </label>
              <label class="checkbox-label">
                <input type="checkbox" v-model="editingTemplate.is_default_for_category">
                Default for category
              </label>
            </div>
          </div>
          <div class="form-group">
            <label class="form-label">Subject template</label>
            <input v-model="editingTemplate.subject_template" type="text" class="form-control">
          </div>
          <div class="form-group">
            <label class="form-label">HTML content</label>
            <textarea
              v-model="editingTemplate.html_content"
              class="form-control monospace"
              rows="14"
            ></textarea>
          </div>
          <div class="form-group">
            <label class="form-label">Plain-text content</label>
            <textarea
              v-model="editingTemplate.text_content"
              class="form-control monospace"
              rows="10"
            ></textarea>
          </div>
          <div class="form-group">
            <label class="form-label">Description (internal note)</label>
            <textarea
              v-model="editingTemplate.description"
              class="form-control"
              rows="2"
            ></textarea>
          </div>
        </div>
        <div class="modal-actions">
          <button class="btn btn-outline" @click="closeEditor">Cancel</button>
          <button
            class="btn btn-primary"
            :disabled="savingTemplate"
            @click="saveTemplate"
          >
            <i class="fas fa-save"></i>
            {{ savingTemplate ? 'Saving...' : 'Save Template' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ───── Preview modal ─────────────────────────────────────────── -->
    <div v-if="previewing" class="modal-overlay" @click.self="previewing = null">
      <div class="modal-content modal-preview">
        <div class="modal-header">
          <h3>Preview: {{ previewing.name }}</h3>
          <button class="modal-close" @click="previewing = null">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="preview-subject">
            <strong>Subject:</strong> {{ previewing.subject_template }}
          </div>
          <div class="preview-html" v-html="previewing.html_content || previewing.text_content"></div>
          <details class="preview-vars" v-if="Object.keys(previewing.available_variables || {}).length">
            <summary>Available variables</summary>
            <ul>
              <li v-for="(desc, name) in previewing.available_variables" :key="name">
                <code>{{ formatVariable(name) }}</code> &mdash; {{ desc }}
              </li>
            </ul>
          </details>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';
import auth from '@/services/auth';

// Default frame templates kept in sync with backend
// services/email_frame.py + migration 0113. Shown as placeholder text so
// admins know what "Leave blank" actually renders.
const DEFAULT_HEADER_PLACEHOLDER = `{{ recipient_salutation }}

A document upload has been requested by {{ client_name }} for your Insured{% if insured_name %}, {{ insured_name }}{% endif %}.

{% if certificate_holder_name %}CERTIFICATE HOLDER:
{{ certificate_holder_name }}{% if certificate_holder_address %}
{{ certificate_holder_address }}{% endif %}
{% endif %}`;

const DEFAULT_FOOTER_PLACEHOLDER = `To upload your documents:
1. Click this secure link: {{ upload_url }}
2. Select your files
3. Click upload

Thank you on behalf of the {{ compliance_team_name }}. This link will expire in {{ deadline_days }} days for security purposes.`;

const BLANK_SETTINGS = {
  from_email: '',
  from_name: '',
  reply_to_email: '',
  logo_url: '',
  brand_color: '#2563eb',
  compliance_team_name: '',
  notification_style_guide: '',
  upload_request_subject_basic: '',
  upload_request_subject_health: '',
  upload_request_subject_gap: '',
  email_frame_header_template: '',
  email_frame_footer_template: '',
  include_legal_footer: true,
  legal_disclaimer: '',
  resolved_compliance_team_name: '',
};

export default {
  name: 'OrgEmailBranding',
  data() {
    return {
      loading: true,
      saving: false,
      saveMsg: '',
      saveMsgType: 'success',
      settings: { ...BLANK_SETTINGS },
      templates: [],
      categories: [],
      editingTemplate: null,
      savingTemplate: false,
      previewing: null,
      defaultHeaderPlaceholder: DEFAULT_HEADER_PLACEHOLDER,
      defaultFooterPlaceholder: DEFAULT_FOOTER_PLACEHOLDER,
    };
  },
  computed: {
    canEdit() {
      // Org admin or platform staff. Read access is granted by the backend
      // permission_classes; we still gate the UI so non-admins see disabled
      // inputs and a "lock" note rather than failing PUTs.
      return auth.hasAnyRole([
        'PLATFORM_ADMIN',
        'PLATFORM_ENGINEER',
        'ORG_ADMIN',
        'HEALTHCARE_ADMIN',
        'ADMIN',
      ]);
    },
    defaultComplianceTeamPlaceholder() {
      return this.settings.resolved_compliance_team_name
        || '{Your Organization} Compliance Team';
    },
  },
  async mounted() {
    await this.loadAll();
  },
  methods: {
    async loadAll() {
      this.loading = true;
      try {
        await Promise.all([
          this.loadSettings(),
          this.loadTemplates(),
          this.loadCategories(),
        ]);
      } finally {
        this.loading = false;
      }
    },
    async loadSettings() {
      try {
        const response = await api.get('/organization/email-settings/');
        this.settings = { ...BLANK_SETTINGS, ...response.data };
      } catch (err) {
        console.error('Failed to load org email settings:', err);
        this.flash('error', 'Failed to load email branding settings.');
      }
    },
    async loadTemplates() {
      try {
        const response = await api.get('/email/templates/');
        this.templates = response.data?.results || [];
      } catch (err) {
        console.error('Failed to load templates:', err);
      }
    },
    async loadCategories() {
      try {
        const response = await api.get('/email/categories/');
        this.categories = response.data || [];
      } catch (err) {
        console.error('Failed to load categories:', err);
      }
    },
    async saveSettings() {
      this.saving = true;
      this.saveMsg = '';
      try {
        const payload = { ...this.settings };
        delete payload.resolved_compliance_team_name;
        delete payload.id;
        delete payload.organization_id;
        const response = await api.put('/organization/email-settings/', payload);
        this.settings = { ...BLANK_SETTINGS, ...response.data };
        this.flash('success', 'Email branding saved. Future sends will use the new wording.');
      } catch (err) {
        console.error('Failed to save settings:', err);
        const msg = err.response?.data?.detail
          || err.response?.data?.error
          || err.message
          || 'Unknown error';
        this.flash('error', `Failed to save: ${msg}`);
      } finally {
        this.saving = false;
      }
    },
    previewTemplate(template) {
      this.previewing = template;
    },
    editTemplate(template) {
      this.editingTemplate = {
        ...template,
        category_id: template.category?.id || null,
      };
    },
    cloneTemplate(template) {
      // Strip id + bump slug + flag as not-default so the user gets a
      // fresh row owned by their org. Backend rejects duplicate slugs.
      this.editingTemplate = {
        ...template,
        id: null,
        slug: this.suggestCloneSlug(template.slug),
        name: `${template.name} (copy)`,
        is_default_for_category: false,
        category_id: template.category?.id || null,
      };
    },
    suggestCloneSlug(slug) {
      const existing = new Set(this.templates.map(t => t.slug));
      let candidate = `${slug}-copy`;
      let n = 2;
      while (existing.has(candidate)) {
        candidate = `${slug}-copy-${n}`;
        n += 1;
      }
      return candidate;
    },
    closeEditor() {
      this.editingTemplate = null;
    },
    async saveTemplate() {
      this.savingTemplate = true;
      try {
        const t = this.editingTemplate;
        const payload = {
          name: t.name,
          slug: t.slug,
          category: t.category_id,
          template_type: t.template_type || 'HTML',
          subject_template: t.subject_template,
          html_content: t.html_content || '',
          text_content: t.text_content || '',
          description: t.description || '',
          is_active: !!t.is_active,
          is_default_for_category: !!t.is_default_for_category,
          requires_preview: !!t.requires_preview,
        };
        if (t.id) {
          await api.put(`/email/templates/${t.id}/`, payload);
        } else {
          await api.post('/email/templates/', payload);
        }
        this.editingTemplate = null;
        await this.loadTemplates();
        this.flash('success', 'Template saved.');
      } catch (err) {
        console.error('Failed to save template:', err);
        const msg = err.response?.data?.detail
          || err.response?.data?.error
          || err.message;
        this.flash('error', `Failed to save template: ${msg}`);
      } finally {
        this.savingTemplate = false;
      }
    },
    async deleteTemplate(template) {
      if (!confirm(`Delete template "${template.name}"? This cannot be undone.`)) {
        return;
      }
      try {
        await api.delete(`/email/templates/${template.id}/`);
        await this.loadTemplates();
        this.flash('success', 'Template deleted.');
      } catch (err) {
        console.error('Failed to delete template:', err);
        const msg = err.response?.data?.error || err.message;
        this.flash('error', `Failed to delete: ${msg}`);
      }
    },
    flash(type, message) {
      this.saveMsg = message;
      this.saveMsgType = type;
      setTimeout(() => {
        this.saveMsg = '';
      }, 5000);
    },
    formatVariable(name) {
      return `{{ ${name} }}`;
    },
    templateRowClass(template) {
      if (!template.is_active) return 'edge-inactive';
      if (!template.owned_by_org) return 'edge-platform';
      if (template.is_default_for_category) return 'edge-default';
      return '';
    },
  },
};
</script>

<style scoped>
.email-branding-page {
  max-width: 960px;
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--p-ink-muted, #64748b);
  text-decoration: none;
  margin-bottom: 0.65rem;
  transition: color var(--p-transition, 180ms ease);
}

.back-link:hover {
  color: var(--p-accent, #1e3a5f);
}

.page-header {
  margin-bottom: 1.5rem;
}

.page-title {
  font-size: 1.35rem;
  font-weight: 700;
  color: var(--p-ink, #1e293b);
  margin: 0 0 0.35rem;
}

.page-subtitle {
  margin: 0;
  font-size: 0.9rem;
  color: var(--p-ink-muted, #64748b);
  line-height: 1.5;
  max-width: 44rem;
}

.page-subtitle strong {
  color: var(--p-ink, #1e293b);
  font-weight: 600;
}

.page-subtitle code {
  background: var(--p-surface-muted, #f8fafc);
  border: 1px solid var(--p-border, #e2e8f0);
  padding: 0.05rem 0.35rem;
  border-radius: 4px;
  font-size: 0.85em;
  color: var(--p-ink, #1e293b);
}

.loading-state {
  padding: 2rem;
  text-align: center;
  color: var(--p-ink-muted, #64748b);
}

.loading-state i {
  margin-right: 0.45rem;
  color: var(--p-accent, #1e3a5f);
}

.settings-card {
  background: var(--p-surface-elevated, #fff);
  border: 1px solid var(--p-border, #e2e8f0);
  border-radius: var(--p-radius, 0.75rem);
  padding: 1.25rem 1.35rem;
  margin-bottom: 1.25rem;
}

.card-header {
  display: flex;
  gap: 0.85rem;
  align-items: flex-start;
  margin-bottom: 1.25rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--p-border, #e2e8f0);
}

.card-header > i {
  color: var(--p-accent, #1e3a5f);
  font-size: 1.15rem;
  margin-top: 0.15rem;
  flex-shrink: 0;
}

.card-title {
  font-size: 1rem;
  font-weight: 700;
  color: var(--p-ink, #1e293b);
  margin: 0 0 0.25rem;
}

.card-desc {
  margin: 0;
  font-size: 0.85rem;
  color: var(--p-ink-muted, #64748b);
  line-height: 1.45;
}

.card-body {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 0.75rem;
}

.form-row .form-group {
  margin-bottom: 0;
}

.form-group {
  margin-bottom: 0.85rem;
}

.form-label {
  display: block;
  font-weight: 600;
  margin-bottom: 0.35rem;
  color: var(--p-ink, #1e293b);
  font-size: 0.82rem;
}

.help-inline {
  font-weight: 400;
  font-size: 0.85em;
  color: var(--p-ink-muted, #64748b);
  margin-left: 0.25rem;
}

.form-control {
  width: 100%;
  padding: 0.55rem 0.75rem;
  border: 1px solid var(--p-border-strong, #cbd5e1);
  border-radius: var(--p-radius-sm, 0.5rem);
  font-size: 0.9rem;
  color: var(--p-ink, #1e293b);
  background: var(--p-surface-muted, #f8fafc);
  box-sizing: border-box;
  transition: border-color var(--p-transition, 180ms ease), box-shadow var(--p-transition, 180ms ease);
}

.form-control:focus {
  outline: none;
  border-color: var(--p-accent, #1e3a5f);
  background: #fff;
  box-shadow: var(--p-focus-ring, 0 0 0 3px color-mix(in srgb, #1e3a5f 25%, transparent));
}

.form-control:disabled {
  background: var(--p-surface-muted, #f8fafc);
  color: var(--p-ink-muted, #64748b);
  opacity: 0.85;
}

.form-control.monospace {
  font-family: 'SF Mono', 'Monaco', 'Menlo', monospace;
  font-size: 0.82rem;
  line-height: 1.55;
}

.help-text {
  display: block;
  font-size: 0.78rem;
  color: var(--p-ink-muted, #64748b);
  margin-top: 0.3rem;
  line-height: 1.5;
}

.help-text code {
  background: var(--p-surface-muted, #f8fafc);
  border: 1px solid var(--p-border, #e2e8f0);
  padding: 0.05rem 0.35rem;
  border-radius: 3px;
  font-size: 0.9em;
}

.save-bar {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  flex-wrap: wrap;
  background: var(--p-surface-muted, #f8fafc);
  border: 1px solid var(--p-border, #e2e8f0);
  border-radius: var(--p-radius, 0.75rem);
  padding: 0.85rem 1.1rem;
  margin-bottom: 1.25rem;
}

.save-msg {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.85rem;
  font-weight: 600;
}

.save-msg.success {
  color: var(--p-success, #16a34a);
}

.save-msg.error {
  color: var(--p-danger, #ef4444);
}

.permission-note {
  margin: 0;
  font-size: 0.82rem;
  color: var(--p-ink-muted, #64748b);
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0.9rem;
  border-radius: var(--p-radius-sm, 0.5rem);
  border: 1px solid transparent;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.88rem;
  text-decoration: none;
  transition: background var(--p-transition, 180ms ease), border-color var(--p-transition, 180ms ease);
}

.btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.btn-primary {
  background: var(--p-accent, #1e3a5f);
  color: var(--p-accent-contrast, #fff);
  border-color: var(--p-accent, #1e3a5f);
}

.btn-primary:hover:not(:disabled) {
  background: var(--p-accent-hover, #162b46);
}

.btn-outline {
  background: var(--p-surface-elevated, #fff);
  border-color: var(--p-border-strong, #cbd5e1);
  color: var(--p-ink, #1e293b);
}

.btn-outline:hover:not(:disabled) {
  border-color: var(--p-accent, #1e3a5f);
  color: var(--p-accent, #1e3a5f);
  background: var(--p-accent-subtle, #eef2f8);
}

.btn-sm {
  padding: 0.35rem 0.55rem;
  font-size: 0.78rem;
}

.btn-danger {
  color: var(--p-danger, #ef4444);
}

.btn-danger:hover:not(:disabled) {
  border-color: var(--p-danger, #ef4444);
  color: var(--p-danger, #ef4444);
  background: #fef2f2;
}

/* Template list — left-edge status */
.templates-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.template-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.85rem 1rem;
  border: 1px solid var(--p-border, #e2e8f0);
  border-left-width: 4px;
  border-radius: var(--p-radius-sm, 0.5rem);
  background: var(--p-surface-elevated, #fff);
  gap: 1rem;
}

.template-row.edge-default {
  border-left-color: #d97706;
}

.template-row.edge-platform {
  border-left-color: var(--p-border-strong, #cbd5e1);
  background: var(--p-surface-muted, #f8fafc);
}

.template-row.edge-inactive {
  border-left-color: var(--p-danger, #ef4444);
  opacity: 0.75;
}

.template-info {
  flex: 1;
  min-width: 0;
}

.template-name {
  font-weight: 600;
  color: var(--p-ink, #1e293b);
  font-size: 0.92rem;
  display: flex;
  align-items: center;
  gap: 0.45rem;
  flex-wrap: wrap;
}

.row-label {
  font-size: 0.68rem;
  padding: 0.1rem 0.4rem;
  border-radius: 4px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.row-label--default {
  background: #fef3c7;
  color: #92400e;
}

.row-label--muted {
  background: var(--p-surface-muted, #f8fafc);
  color: var(--p-ink-muted, #64748b);
  border: 1px solid var(--p-border, #e2e8f0);
}

.row-label--inactive {
  background: #fef2f2;
  color: #991b1b;
}

.template-meta {
  display: flex;
  gap: 1rem;
  margin-top: 0.3rem;
  font-size: 0.78rem;
  color: var(--p-ink-muted, #64748b);
  flex-wrap: wrap;
}

.template-slug {
  background: var(--p-surface-muted, #f8fafc);
  border: 1px solid var(--p-border, #e2e8f0);
  padding: 0.1rem 0.4rem;
  border-radius: 4px;
  color: var(--p-ink, #1e293b);
  font-size: 0.78rem;
}

.template-subject {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 50ch;
}

.template-actions {
  display: flex;
  gap: 0.3rem;
  flex-shrink: 0;
}

.empty-state {
  text-align: center;
  padding: 2rem 1rem;
  color: var(--p-ink-muted, #64748b);
}

.empty-state i {
  font-size: 1.8rem;
  margin-bottom: 0.6rem;
  display: block;
  color: var(--p-border-strong, #cbd5e1);
}

/* Modals */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.modal-content {
  background: var(--p-surface-elevated, #fff);
  border: 1px solid var(--p-border, #e2e8f0);
  border-radius: var(--p-radius, 0.75rem);
  width: 100%;
  max-width: 760px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.modal-preview {
  max-width: 820px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--p-border, #e2e8f0);
}

.modal-header h3 {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: var(--p-ink, #1e293b);
}

.modal-close {
  background: none;
  border: none;
  font-size: 1rem;
  color: var(--p-ink-muted, #64748b);
  cursor: pointer;
  padding: 0.25rem;
}

.modal-close:hover {
  color: var(--p-ink, #1e293b);
}

.modal-body {
  padding: 1.15rem 1.25rem;
  overflow-y: auto;
  flex: 1;
}

.modal-actions {
  display: flex;
  gap: 0.65rem;
  justify-content: flex-end;
  padding: 0.85rem 1.25rem;
  border-top: 1px solid var(--p-border, #e2e8f0);
  background: var(--p-surface-muted, #f8fafc);
}

.form-group--checkbox-row {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  justify-content: flex-end;
  padding-bottom: 0.5rem;
}

.checkbox-label {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.88rem;
  color: var(--p-ink, #1e293b);
  cursor: pointer;
}

.preview-subject {
  background: var(--p-surface-muted, #f8fafc);
  border: 1px solid var(--p-border, #e2e8f0);
  border-radius: var(--p-radius-sm, 0.5rem);
  padding: 0.7rem 1rem;
  margin-bottom: 1rem;
  font-size: 0.92rem;
}

.preview-html {
  border: 1px solid var(--p-border, #e2e8f0);
  border-radius: var(--p-radius-sm, 0.5rem);
  padding: 1rem;
  background: #fff;
  font-size: 0.9rem;
  line-height: 1.6;
  white-space: pre-line;
}

.preview-vars {
  margin-top: 1.2rem;
  border-top: 1px solid var(--p-border, #e2e8f0);
  padding-top: 1rem;
}

.preview-vars summary {
  cursor: pointer;
  font-weight: 600;
  color: var(--p-ink-muted, #64748b);
  font-size: 0.85rem;
}

.preview-vars ul {
  margin: 0.6rem 0 0 1rem;
  padding: 0;
  font-size: 0.83rem;
  color: var(--p-ink-muted, #64748b);
}

.preview-vars li {
  margin-bottom: 0.3rem;
}

@media (max-width: 768px) {
  .form-row {
    grid-template-columns: 1fr;
  }

  .template-row {
    flex-direction: column;
    align-items: stretch;
  }

  .template-actions {
    justify-content: flex-end;
  }
}
</style>
